import { useMemo, useState, type ReactNode } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type {
  FilterOption,
  PendingTask,
  PendingTaskRow,
  PendingTasksQuery,
  PendingTasksSortColumn,
  PendingTaskTier,
  SortDirection,
} from '@/types/pendingTasks';
import { usePendingTasks } from '@/hooks/react-query/usePendingTasks';
import { PendingTasksRefreshControls } from './PendingTasksRefreshControls';
import { dueDisplayFor } from './pendingTaskTable';

const PER_PAGE = 10;

const STATUS_TABS: { id: PendingTaskTier | ''; label: string }[] = [
  { id: '', label: 'All' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'soon', label: 'Due soon' },
];

const SORT_HEADERS: { column: PendingTasksSortColumn; label: string }[] = [
  { column: 'task', label: 'Task' },
  { column: 'fund', label: 'Fund' },
  { column: 'entity', label: 'Company' },
  { column: 'category', label: 'Category' },
  { column: 'due', label: 'Due' },
];

const GRID_COLS =
  'grid grid-cols-[minmax(180px,1fr)_112px_212px_112px_130px_26px] items-center';

// Color-coded category pills so each kind of work is scannable at a glance.
const CATEGORY_PILL_STYLES: Record<string, string> = {
  'Capital Calls':
    'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  Transactions:
    'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  Convertibles:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Distributions:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Reporting: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};
const DEFAULT_CATEGORY_PILL = 'bg-muted text-muted-foreground';

function toggledDirection(direction: SortDirection): SortDirection {
  return direction === 'asc' ? 'desc' : 'asc';
}

function taskNoun(count: number): string {
  return count === 1 ? 'task' : 'tasks';
}

function emptyStateCopy(
  hasAnyTasks: boolean,
  statusFilter: PendingTaskTier | '',
  hasFieldFilter: boolean
): { title: string; body: string } {
  if (!hasAnyTasks) {
    return {
      title: 'No pending tasks',
      body: 'Nothing needs attention across your funds right now.',
    };
  }
  if (statusFilter === 'overdue' && !hasFieldFilter) {
    return {
      title: 'Nothing overdue',
      body: 'No tasks are past their due date right now.',
    };
  }
  if (statusFilter === 'soon' && !hasFieldFilter) {
    return {
      title: 'Nothing due soon',
      body: 'No tasks fall due within the next two weeks.',
    };
  }
  return {
    title: 'No tasks match these filters',
    body: 'Try adjusting the status, fund, company, or category selections.',
  };
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function SortIcon({ active, asc }: { active: boolean; asc: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 transition-transform duration-150 ${
        active ? 'text-brand opacity-100' : 'text-muted-foreground opacity-30'
      } ${active && asc ? 'rotate-180' : ''}`}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function DueBadge({ row }: { row: PendingTaskRow }) {
  const { tier, text, title } = dueDisplayFor(row);
  if (tier === 'overdue') {
    return (
      <span
        className="num inline-flex items-center gap-1.5 rounded-full bg-red-500/10 px-2.5 py-0.5 text-[11.5px] font-bold text-red-700 dark:bg-red-500/15 dark:text-red-400"
        title={title}
      >
        <span className="h-[5px] w-[5px] rounded-full bg-red-600 dark:bg-red-500" />
        {text}
      </span>
    );
  }
  return (
    <span
      className="num inline-flex items-center gap-1.5 rounded-full bg-amber-600/[0.13] px-2.5 py-0.5 text-[11.5px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
      title={title}
    >
      <span className="h-[5px] w-[5px] rounded-full bg-amber-600 dark:bg-amber-500" />
      {text}
    </span>
  );
}

interface FilterDropdownProps {
  icon: ReactNode;
  placeholder: string;
  options: FilterOption[];
  value: string;
  onSelect: (id: string) => void;
}

function FilterDropdown({
  icon,
  placeholder,
  options,
  value,
  onSelect,
}: FilterDropdownProps) {
  const active = !!value;
  const selectedLabel =
    options.find((option) => option.id === value)?.label ?? placeholder;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`flex h-9 max-w-[240px] items-center gap-1.5 rounded-lg border px-2.5 text-[13.5px] ${
            active
              ? 'border-brand/35 bg-brand/[0.09] font-semibold text-brand'
              : 'border-border bg-card font-medium text-muted-foreground'
          }`}
        >
          {icon}
          <span className="min-w-0 flex-1 truncate text-left">
            {selectedLabel}
          </span>
          <ChevronDown />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[210px]">
        <DropdownMenuItem
          onClick={() => onSelect('')}
          className={!value ? 'font-semibold text-brand' : ''}
        >
          {placeholder}
        </DropdownMenuItem>
        {options.map((option) => (
          <DropdownMenuItem
            key={option.id}
            onClick={() => onSelect(option.id)}
            className={option.id === value ? 'font-semibold text-brand' : ''}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface PendingTasksTableProps {
  onSelectTask: (task: PendingTask) => void;
  lastUpdated: string | null;
  onRefresh: () => void;
  isRefreshing: boolean;
}

export function PendingTasksTable({
  onSelectTask,
  lastUpdated,
  onRefresh,
  isRefreshing,
}: PendingTasksTableProps) {
  const [statusFilter, setStatusFilter] = useState<PendingTaskTier | ''>('');
  const [fundId, setFundId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [category, setCategory] = useState('');
  const [sortColumn, setSortColumn] =
    useState<PendingTasksSortColumn>('urgency');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);

  const query: PendingTasksQuery = useMemo(
    () => ({
      page: currentPage,
      perPage: PER_PAGE,
      sort: sortColumn,
      direction: sortDirection,
      tier: statusFilter,
      fundId,
      companyId,
      category,
    }),
    [
      currentPage,
      sortColumn,
      sortDirection,
      statusFilter,
      fundId,
      companyId,
      category,
    ]
  );

  const { data: pageData, isLoading, isError } = usePendingTasks(query);

  const items = pageData?.items ?? [];
  const total = pageData?.total ?? 0;
  const filteredTotal = pageData?.filtered_total ?? 0;
  const overdueCount = pageData?.overdue_count ?? 0;
  const soonCount = pageData?.soon_count ?? 0;
  const fundOptions = pageData?.filter_options?.funds ?? [];
  const companyOptions = pageData?.filter_options?.companies ?? [];
  const categoryOptions = pageData?.filter_options?.categories ?? [];
  const hasAnyTasks = fundOptions.length > 0;

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const page = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (page - 1) * PER_PAGE;
  const endIndex = startIndex + items.length;

  const hasFieldFilter = !!fundId || !!companyId || !!category;
  const hasActiveFilter = hasFieldFilter || !!statusFilter;

  const goToFirstPage = () => setCurrentPage(1);

  const handleSort = (column: PendingTasksSortColumn) => {
    setSortColumn(column);
    setSortDirection((current) =>
      sortColumn === column ? toggledDirection(current) : 'asc'
    );
    goToFirstPage();
  };

  const clearFilters = () => {
    setFundId('');
    setCompanyId('');
    setCategory('');
    setStatusFilter('');
    goToFirstPage();
  };

  const summaryParts: string[] = [];
  if (overdueCount) summaryParts.push(`${overdueCount} overdue`);
  if (soonCount) summaryParts.push(`${soonCount} due soon`);
  const summarySuffix = summaryParts.length
    ? `  ·  ${summaryParts.join('  ·  ')}`
    : '';
  const resultSummary =
    filteredTotal === 0
      ? 'No tasks'
      : `${filteredTotal} ${taskNoun(filteredTotal)}${summarySuffix}`;

  const paginationLabel =
    totalPages === 1
      ? `Showing all ${total} ${taskNoun(total)}`
      : `Showing ${startIndex + 1}–${endIndex} of ${total} tasks`;

  const tabCounts: Record<PendingTaskTier | '', number | null> = {
    '': null,
    overdue: overdueCount,
    soon: soonCount,
  };

  const emptyCopy = emptyStateCopy(hasAnyTasks, statusFilter, hasFieldFilter);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">Could not load pending tasks</p>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className="mb-5">
        <h1 className="text-[30px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
          Pending Tasks
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Everything that needs attention across all funds, with the most
          actionable items first.
        </p>
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Status segmented control */}
        <div className="inline-flex items-center gap-0.5 rounded-[9px] bg-muted p-[3px]">
          {STATUS_TABS.map((tab) => {
            const isActive = statusFilter === tab.id;
            const count = tabCounts[tab.id];
            return (
              <button
                key={tab.id || 'all'}
                type="button"
                onClick={() => {
                  setStatusFilter(tab.id);
                  goToFirstPage();
                }}
                className={`inline-flex h-[30px] items-center gap-1.5 rounded-[7px] px-3 text-[13px] ${
                  isActive
                    ? 'bg-card font-semibold text-foreground shadow-sm'
                    : 'font-medium text-muted-foreground'
                }`}
              >
                {tab.label}
                {count !== null && (
                  <span
                    className={`num inline-flex h-4 items-center rounded-full px-1.5 text-[11px] font-bold ${
                      isActive
                        ? 'bg-brand/15 text-brand'
                        : 'bg-muted-foreground/15 text-muted-foreground'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="h-[26px] w-px bg-border" />

        <FilterDropdown
          placeholder="Fund"
          value={fundId}
          options={fundOptions}
          onSelect={(id) => {
            setFundId(id);
            setCompanyId('');
            goToFirstPage();
          }}
          icon={
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0"
            >
              <rect width="20" height="14" x="2" y="7" rx="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          }
        />
        <FilterDropdown
          placeholder="Company"
          value={companyId}
          options={companyOptions}
          onSelect={(id) => {
            setCompanyId(id);
            goToFirstPage();
          }}
          icon={
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0"
            >
              <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
              <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
              <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 0-2 2h-2" />
              <path d="M10 6h4" />
              <path d="M10 10h4" />
              <path d="M10 14h4" />
            </svg>
          }
        />
        <FilterDropdown
          placeholder="Category"
          value={category}
          options={categoryOptions}
          onSelect={(id) => {
            setCategory(id);
            goToFirstPage();
          }}
          icon={
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
          }
        />

        {hasActiveFilter && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-[13px] text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
            Clear
          </button>
        )}

        <div className="ml-auto flex items-center gap-3">
          <span className="num text-[12.5px] text-muted-foreground">
            {resultSummary}
          </span>
          <PendingTasksRefreshControls
            lastUpdated={lastUpdated}
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
          />
        </div>
      </div>

      {/* Table card */}
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        {items.length > 0 ? (
          <div>
            {/* Header */}
            <div className={`${GRID_COLS} border-b border-border bg-muted/50`}>
              {SORT_HEADERS.map((header, index) => {
                const isActive = sortColumn === header.column;
                return (
                  <button
                    key={header.column}
                    type="button"
                    onClick={() => handleSort(header.column)}
                    className={`flex select-none items-center gap-1.5 py-2.5 text-left hover:bg-muted ${
                      index === 0 ? 'pl-7 pr-4' : 'px-2'
                    }`}
                  >
                    <span
                      className={`text-[11px] uppercase tracking-[0.05em] ${
                        isActive
                          ? 'font-semibold text-brand'
                          : 'font-medium text-muted-foreground'
                      }`}
                    >
                      {header.label}
                    </span>
                    <SortIcon active={isActive} asc={sortDirection === 'asc'} />
                  </button>
                );
              })}
              <div />
            </div>

            {/* Rows */}
            {items.map((row) => (
              <button
                key={row.task.task_key}
                type="button"
                onClick={() => onSelectTask(row.task)}
                className={`${GRID_COLS} w-full cursor-pointer border-b border-border text-left hover:bg-accent/50`}
              >
                <span className="min-w-0 py-2.5 pl-7 pr-4">
                  <span className="block truncate text-[13.5px] font-medium text-foreground">
                    {row.display_title}
                  </span>
                  {row.task.description && (
                    <span className="mt-0.5 block truncate text-[11.5px] text-muted-foreground">
                      {row.task.description}
                    </span>
                  )}
                </span>
                <span className="truncate px-2 py-2.5 text-[12.5px] text-muted-foreground">
                  {row.task.fund_name}
                </span>
                <span
                  className="truncate px-2 py-2.5 text-[12.5px] text-muted-foreground"
                  title={row.entity_name}
                >
                  {row.entity_name}
                </span>
                <span className="px-2 py-2.5">
                  <span
                    className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      CATEGORY_PILL_STYLES[row.category_label] ??
                      DEFAULT_CATEGORY_PILL
                    }`}
                  >
                    {row.category_label}
                  </span>
                </span>
                <span className="px-2 py-2.5">
                  <DueBadge row={row} />
                </span>
                <span className="flex items-center justify-end py-2.5 pl-1 pr-5">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-muted-foreground/40"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </span>
              </button>
            ))}

            {/* Footer / pagination */}
            <div className="flex min-h-[46px] items-center justify-between border-t border-border bg-muted/50 px-7 py-2.5">
              <span className="num text-[12.5px] text-muted-foreground">
                {paginationLabel}
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="flex h-[30px] w-[30px] items-center justify-center rounded-md border border-border bg-card hover:bg-accent disabled:pointer-events-none disabled:opacity-35"
                    aria-label="Previous page"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m15 18-6-6 6-6" />
                    </svg>
                  </button>
                  <span className="num min-w-[64px] text-center text-[13px] font-medium text-foreground">
                    {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={page >= totalPages}
                    className="flex h-[30px] w-[30px] items-center justify-center rounded-md border border-border bg-card hover:bg-accent disabled:pointer-events-none disabled:opacity-35"
                    aria-label="Next page"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3.5 px-10 py-20 text-center">
            <div className="flex h-[52px] w-[52px] items-center justify-center rounded-[14px] bg-muted text-muted-foreground">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div>
              <div className="mb-1.5 text-[15px] font-semibold text-foreground">
                {emptyCopy.title}
              </div>
              <div className="mx-auto max-w-[300px] text-[13px] leading-relaxed text-muted-foreground">
                {emptyCopy.body}
              </div>
            </div>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={clearFilters}
                className="mt-1 inline-flex items-center rounded-lg bg-brand px-[18px] py-2 text-[13.5px] font-semibold text-brand-foreground hover:bg-brand/90"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
